import { FormEvent, useEffect, useState } from "react";
import type { ServicePricingType } from "@gigflow/shared";
import { apiRequest, formatMoney } from "../api";
import { useAuth } from "../auth";

type Service = {
  id: string;
  name: string;
  description?: string | null;
  pricingType: ServicePricingType;
  priceCents?: number | null;
  durationMinutes?: number | null;
  isArchived: boolean;
  isBookableOnline: boolean;
};

export function ServicesPage() {
  const { businessId } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    pricingType: "FIXED" as ServicePricingType,
    priceCents: "",
    durationMinutes: "60",
  });

  async function load() {
    if (!businessId) return;
    const data = await apiRequest<{ services: Service[] }>(
      `/businesses/${businessId}/services?includeArchived=1`,
    );
    setServices(data.services);
  }

  useEffect(() => {
    void load().catch((err: Error) => setError(err.message));
  }, [businessId]);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    if (!businessId) return;
    setPending(true);
    setError(null);
    try {
      await apiRequest(`/businesses/${businessId}/services`, {
        method: "POST",
        body: JSON.stringify({
          name: form.name,
          description: form.description || undefined,
          pricingType: form.pricingType,
          priceCents: form.priceCents
            ? Math.round(Number(form.priceCents) * 100)
            : undefined,
          durationMinutes: form.durationMinutes
            ? Number(form.durationMinutes)
            : undefined,
          isBookableOnline: true,
        }),
      });
      setForm({
        name: "",
        description: "",
        pricingType: "FIXED",
        priceCents: "",
        durationMinutes: "60",
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setPending(false);
    }
  }

  async function archive(serviceId: string) {
    if (!businessId) return;
    setPending(true);
    try {
      await apiRequest(
        `/businesses/${businessId}/services/${serviceId}/archive`,
        { method: "POST" },
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Archive failed");
    } finally {
      setPending(false);
    }
  }

  if (!businessId)
    return (
      <section>
        <p className="notice">No business selected.</p>
      </section>
    );

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">Services</p>
          <h1>Your service catalog</h1>
        </div>
      </header>

      {error ? <p className="notice error">{error}</p> : null}

      <section>
        <h2>Create service</h2>
        <form className="form-grid" onSubmit={onCreate}>
          <label>
            Name
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            Description
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </label>
          <div className="form-grid two">
            <label>
              Pricing type
              <select
                value={form.pricingType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    pricingType: e.target.value as ServicePricingType,
                  })
                }
              >
                <option value="FIXED">Fixed</option>
                <option value="HOURLY">Hourly</option>
                <option value="STARTING_AT">Starting at</option>
                <option value="QUOTE_REQUIRED">Quote required</option>
              </select>
            </label>
            <label>
              Price (USD)
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.priceCents}
                onChange={(e) =>
                  setForm({ ...form, priceCents: e.target.value })
                }
              />
            </label>
          </div>
          <label>
            Duration (minutes)
            <input
              type="number"
              min={15}
              value={form.durationMinutes}
              onChange={(e) =>
                setForm({ ...form, durationMinutes: e.target.value })
              }
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={pending}>
            {pending ? "Saving..." : "Add service"}
          </button>
        </form>
      </section>

      <section>
        <h2>All services</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Pricing</th>
              <th>Duration</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr key={service.id}>
                <td>
                  <strong>{service.name}</strong>
                  {service.description ? (
                    <div className="notice">{service.description}</div>
                  ) : null}
                </td>
                <td>
                  {service.pricingType} · {formatMoney(service.priceCents)}
                </td>
                <td>
                  {service.durationMinutes
                    ? `${service.durationMinutes} min`
                    : "—"}
                </td>
                <td>{service.isArchived ? "Archived" : "Active"}</td>
                <td className="actions">
                  {!service.isArchived ? (
                    <button
                      className="btn btn-danger"
                      type="button"
                      disabled={pending}
                      onClick={() => void archive(service.id)}
                    >
                      Archive
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
